import { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import CountrySelect from '../../common/CustomSelect/CountrySelect/CountrySelect';
import styles from './AddressUpdateModal.module.css';
import { addInfluencerAddress, getLocationDetails, updateInfluencerAddress } from '../../../services/dashboard.service';
import { FormGroup, AddressLineOneField } from '../../common';
import { Heading } from '../../styledComponents';
import ArrowLeft from '../../../assets/icons/ArrowLeft';
import ModalCommonButtonsContainer from '../../common/ModalComponent/ModalCommonButtonsContainer';
import useIsMobile from '../../../hooks/useIsMobile';
import { handleAddressInputChange, isAddressLineOneValid } from '../../../utils/validations';

type AddressUpdateModalProps = {
  address: any;
  setShowUpdateAddressModal: React.Dispatch<React.SetStateAction<boolean>>;
  fetchHomePageData: () => void;
  profileId: string;
  withoutModal?: boolean;
  modalTitle?: React.ReactNode;
  onSaveComplete?: () => unknown;
  actionButtonText?: string;
  cancelButtonText?: string;
  showBackButton?: boolean;
  handleBack?: () => void;
  showLoading?: boolean;
  showSuccessMessage?: boolean;
};

type AddressDetailsType = {
  fullName: string;
  addressLineOne: string;
  addressLineTwo: string;
  pincode: string;
  city: string;
  state: string;
  country: string;
};

type AddressBodyType = {
  fullName: string;
  addressLineOne: string;
  addressLineTwo: string;
  pincode: string;
  city: string;
  state: string;
  country: string;
  addressId?: string;
  influencerId?: string;
};

export default function AddressUpdateModal({
  address,
  setShowUpdateAddressModal,
  fetchHomePageData,
  profileId,
  withoutModal = false,
  modalTitle,
  onSaveComplete,
  actionButtonText = 'Save',
  cancelButtonText = 'Cancel',
  showBackButton = false,
  handleBack = () => {},
  showLoading = false,
  showSuccessMessage = false,
}: AddressUpdateModalProps) {
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addressDetails, setAddressDetails] = useState<AddressDetailsType>({
    fullName: address?.fullName,
    addressLineOne: address?.addressLineOne,
    addressLineTwo: address?.addressLineTwo,
    pincode: address?.pincode,
    city: address?.city,
    state: address?.state,
    country: address?.country,
  });

  const modalRef = useRef<null | HTMLDivElement>(null);

  const handleOutsideClick = (e: any) => {
    if (!modalRef.current?.contains(e.target)) {
      setShowUpdateAddressModal(false);
    }
  };

  const checkIfAddressPresent = () => {
    const { addressLineOne, pincode, city, country } = addressDetails;
    if (!addressLineOne || !pincode || !city || !country) return false;
    if (!isAddressLineOneValid(addressLineOne)) return false;
    return true;
  };

  const handleUpdateAddress = async () => {
    setIsLoading(true);
    const body: AddressBodyType = {
      fullName: addressDetails?.fullName || '',
      addressLineOne: addressDetails?.addressLineOne || '',
      addressLineTwo: addressDetails?.addressLineTwo || '',
      pincode: addressDetails?.pincode || '',
      city: addressDetails?.city || '',
      state: addressDetails?.state || '',
      country: addressDetails?.country || '',
    };

    if (!address) {
      body.influencerId = profileId;
      const response = await addInfluencerAddress(body);
      if (response.status) {
        if (showSuccessMessage) {
          toast.success(response.data.message);
        }
        setShowUpdateAddressModal(false);
        fetchHomePageData();
        onSaveComplete?.();
      }
    } else {
      body.addressId = address._id;
      const response = await updateInfluencerAddress(body);
      if (response.status) {
        if (showSuccessMessage) {
          toast.success(response.data.message);
        }
        setShowUpdateAddressModal(false);
        fetchHomePageData();
        onSaveComplete?.();
      }
    }
    setIsLoading(false);
  };

  const handleFetchLocation = async (zipcode: string) => {
    const data = await getLocationDetails(zipcode);
    setAddressDetails((prev) => ({ ...prev, ...data }));
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleAddressInputChange(e, setAddressDetails, handleFetchLocation);
  };

  const renderModalTitle = () => {
    if (modalTitle) return modalTitle;

    return <>{!address ? 'Add' : 'Edit'} Address</>;
  };

  const fontSize = isMobile ? '14px' : '16px';
  const labelFontSize = isMobile ? '12px' : '14px';
  const renderContent = () => {
    return (
      <div className={styles.formSection}>
        <div>
          {showBackButton && (
            <button className={styles.modalBackButton} onClick={handleBack} type="button">
              <ArrowLeft width={30} height={30} />
            </button>
          )}
          <Heading as="h3" fontWeight="500" fontSize="16px" color="#353535" textTransform="uppercase">
            {renderModalTitle()}
          </Heading>
        </div>
        <div className={`${isMobile ? 'p-4' : 'p-6'} pb-0 ${styles.addressUpdateModalFormSection}`}>
          <FormGroup
            id="fullName"
            name="fullName"
            label="Full Name"
            type="text"
            placeholder="Your Full Name"
            value={addressDetails.fullName}
            onChange={handleInputChange}
            required
            className="pb-2 pt-0"
            fontSize={fontSize}
            labelFontSize={labelFontSize}
          />
          <AddressLineOneField
            label="Address"
            placeholder="e.g. Empire State Building"
            value={addressDetails.addressLineOne}
            onChange={handleInputChange}
            required
            autoFocus
            className="pb-2 pt-0"
            fontSize={fontSize}
            labelFontSize={labelFontSize}
          />
          <div className={`${styles.group} pb-2 pt-0`}>
            <FormGroup
              id="addressLineTwo"
              name="addressLineTwo"
              label="Apartment, suite, etc"
              type="text"
              placeholder="e.g. 10th floor"
              value={addressDetails.addressLineTwo}
              onChange={handleInputChange}
              required
              className="p-0"
              fontSize={fontSize}
              labelFontSize={labelFontSize}
            />
            <FormGroup
              id="pincode"
              name="pincode"
              label="Postal Code"
              type="text"
              placeholder="e.g. 10001"
              value={addressDetails.pincode}
              onChange={handleInputChange}
              required
              className="p-0"
              fontSize={fontSize}
              labelFontSize={labelFontSize}
            />
          </div>
          <FormGroup
            id="city"
            name="city"
            label="City"
            type="text"
            placeholder="e.g. New York"
            value={addressDetails.city}
            onChange={handleInputChange}
            required
            className="pb-2 pt-0"
            fontSize={fontSize}
            labelFontSize={labelFontSize}
          />
          <FormGroup
            id="state"
            name="state"
            label="State"
            type="text"
            placeholder="e.g. New York"
            value={addressDetails.state}
            onChange={handleInputChange}
            className="pb-2 pt-0"
            fontSize={fontSize}
            labelFontSize={labelFontSize}
          />
          <CountrySelect
            value={addressDetails.country}
            setValue={(value: string) => setAddressDetails({ ...addressDetails, country: value })}
          />
        </div>

        <ModalCommonButtonsContainer
          submitButtonText={actionButtonText}
          cancelButtonText={cancelButtonText}
          onClose={() => setShowUpdateAddressModal(false)}
          onSave={handleUpdateAddress}
          disabled={!checkIfAddressPresent()}
          fontSize={fontSize}
          loading={isLoading || showLoading}
          disabledTooltipText="Add your address to continue"
          className={`${isMobile ? 'p-4 flex flex-col-reverse' : 'p-6'}`}
        />
      </div>
    );
  };

  if (withoutModal) {
    return renderContent();
  }

  return (
    <div className={styles.modalWrapper} onClick={handleOutsideClick} role="button" tabIndex={0}>
      <div className={styles.modal} ref={modalRef}>
        {renderContent()}
      </div>
    </div>
  );
}
